import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ProductReview } from 'src/app/extensions/rating/models/product-reviews/product-review.model';

import { Attribute } from 'ish-core/models/attribute/attribute.model';
import { BreadcrumbItem } from 'ish-core/models/breadcrumb-item/breadcrumb-item.interface';
import { Pricing } from 'ish-core/models/price/price.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { ProductHelper } from 'ish-core/models/product/product.model';
import { SeoSchemaHelper } from 'ish-core/models/seo-schema/seo-schema.helper';

export interface JsonLdSchemaModel {
  productPage?: object;
  categoryPage?: object;
  contentPage?: object;
  faqPage?: object;
  breadCrumbData?: object;
}

export interface FAQAttribute {
  question: string;
  answer: string;
}

export interface JsonLdBreadcrumbItemElement {
  '@type'?: string;
  position?: number;
  name?: string;
  item?: string | unknown[];
}

@Injectable({ providedIn: 'root' })
export class JsonLdService {
  private jsonLdSchema: JsonLdSchemaModel = {};

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private appBaseHref: string,
    private translateService: TranslateService
  ) {}

  // eslint-disable-next-line complexity
  setProductData(product: ProductView, price: Pricing, reviews: ProductReview[]) {
    const images = product?.images
      ?.filter(image => image?.typeID === 'L')
      .map(image => image?.effectiveUrl)
      .filter(url => !!url);

    const isMasterOrRetail = ProductHelper.isMasterProduct(product) || ProductHelper.isRetailSet(product);

    const currency = price?.minSalePrice?.currency || price?.salePrice?.currency || 'USD';

    const priceValue =
      isMasterOrRetail && price?.minSalePrice
        ? price.minSalePrice.value
        : price?.listPrice?.value > price?.salePrice?.value
        ? price?.listPrice?.value
        : price?.salePrice?.value;

    const lowPriceValue =
      isMasterOrRetail && price?.minSalePrice
        ? price.minSalePrice.value
        : price?.listPrice?.value < price?.salePrice?.value
        ? price?.listPrice?.value
        : price?.salePrice?.value;

    const reviewList = reviews?.length
      ? reviews.map(r => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: r.showAuthorNameFlag ? `${r.authorFirstName} ${r.authorLastName}` : 'Anonymous',
          },
          datePublished: r.creationDate ? new Date(r.creationDate).toISOString().split('T')[0] : undefined,
          reviewBody: r.content,
          name: r.title,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r.rating.toString(),
            bestRating: '5',
          },
        }))
      : undefined;

    const aggregateRating = reviews?.length
      ? {
          '@type': 'AggregateRating',
          ratingValue: (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1),
          reviewCount: reviews.length.toString(),
        }
      : undefined;

    const productSchema: any = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product?.name,
      image: images,
      description: product?.shortDescription,
      sku: product?.sku,
      mpn: product?.manufacturer,
      availability: product?.available ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
      brand: {
        '@type': 'Brand',
        name: product?.manufacturer,
      },
      offers: {
        '@type': 'AggregateOffer',
        url: this.document.location.href.replace('http://', 'https://').replace(/;[^\?*]+/g, ''),
        priceCurrency: currency,
        price: lowPriceValue,
        highPrice: priceValue,
        lowPrice: lowPriceValue,
        offerCount: '1',
        availability: product?.available ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
        itemCondition: 'http://schema.org/NewCondition',
      },
      ...(aggregateRating && { aggregateRating }),
      ...(reviewList && { review: reviewList }),
    };

    const attributes = product.attributeGroups?.['SEO_ATTRIBUTES']?.attributes ?? [];
    const seoFaqs = this.mapAttributesToFaq(
      attributes.map(attr => ({
        name: attr.name,
        value: typeof attr.value === 'string' ? attr.value : String(attr.value ?? ''),
      }))
    );

    this.setFaqPageData(SeoSchemaHelper.generateFaqSchemaDataFromAttributes(seoFaqs));

    this.setProductPageData(productSchema);
  }
  mapAttributesToFaq(attributes: { name: string; value: string }[]): FAQAttribute[] {
    const faqs: FAQAttribute[] = [];

    for (let i = 1; i <= 5; i++) {
      const q = attributes.find(attr => attr.name === `SEO_FAQ_Q${i}`)?.value?.trim();
      const a = attributes.find(attr => attr.name === `SEO_FAQ_A${i}`)?.value?.trim();
      if (q && a) {
        faqs.push({ question: q, answer: a });
      }
    }

    return faqs;
  }
  mapToCategoryFAQAttributes(attributes: Attribute[]): FAQAttribute[] {
    const faqs: FAQAttribute[] = [];

    // Group Q&A by index (Q1, A1, Q2, A2, etc.)
    const faqMap: Record<string, Partial<FAQAttribute>> = {};

    attributes.forEach(attr => {
      const match = attr.name.match(/SEO_FAQ_(Q|A)(\d+)/);
      if (match) {
        const [, type, index] = match;
        if (!faqMap[index]) faqMap[index] = {};

        if (type === 'Q') {
          faqMap[index].question = (attr.value as string).trim();
        } else if (type === 'A') {
          faqMap[index].answer = (attr.value as string).trim();
        }
      }
    });

    // Convert the grouped map into array
    Object.keys(faqMap).forEach(key => {
      const { question = '', answer = '' } = faqMap[key];
      faqs.push({ question, answer });
    });

    return faqs;
  }

  setProductPageData(data: object) {
    if (this.isNotEmpty(data)) {
      this.jsonLdSchema = {
        ...this.jsonLdSchema,
        productPage: [{ ...data }],
      };
    }
  }

  setContentPageData(data: object[]) {
    if (this.isNotEmpty(data)) {
      this.jsonLdSchema = {
        ...this.jsonLdSchema,
        contentPage: data,
      };
    }
  }

  setFaqPageData(data: object) {
    if (this.isNotEmpty(data)) {
      this.jsonLdSchema = {
        ...this.jsonLdSchema,
        faqPage: [data],
      };
    }
  }

  setBreadcrumbData(breadcrumbItem: BreadcrumbItem[]) {
    const host = this.document.location.origin;

    const itemListElement: JsonLdBreadcrumbItemElement[] = [
      {
        '@type': 'ListItem',
        position: 1,
        name: `${this.translateService.instant('common.home.link')}`,
        item: this.generateBreadcrumbItemUrl(host, this.appBaseHref)
          .replace('http://', 'https://')
          .replace(/;[^\?*]+/g, ''),
      },
    ];

    breadcrumbItem.forEach((element, index) => {
      itemListElement.push({
        '@type': 'ListItem',
        position: index + 2,
        name: element?.text ?? element?.key,
        item: element?.link
          ? this.generateBreadcrumbItemUrl(host, this.appBaseHref, element?.link as string)
              .replace('http://', 'https://')
              .replace(/;[^\?*]+/g, '')
          : undefined,
      });
    });

    this.jsonLdSchema = {
      ...this.jsonLdSchema,
      breadCrumbData: [
        {
          '@context': 'https://schema.org/',
          '@type': 'BreadcrumbList',
          itemListElement,
        },
      ],
    };
  }

  toJson() {
    const ldJsonSchema: object[] = [];

    if (this.isNotEmpty(this.jsonLdSchema)) {
      Object.entries(this.jsonLdSchema).forEach(([, schemas]) =>
        (schemas as object[]).forEach(schema => ldJsonSchema.push(schema))
      );
    }

    try {
      if (ldJsonSchema.length) {
        return JSON.stringify(ldJsonSchema);
      }
    } catch (error) {
      return;
    }

    return;
  }

  render(doc: Document) {
    const json = this.toJson();
    if (json) {
      const script = doc.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.textContent = json;
      doc.head.appendChild(script);
    }
  }

  private isNotEmpty(data: object) {
    if (!data) {
      return false;
    }

    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        return true;
      }
    }

    return false;
  }

  private generateBreadcrumbItemUrl(host: string, baseHref: string, elementLink?: string): string {
    const elLink = elementLink ? elementLink.replace(/^(?!\/)/, '/') : '';
    return `${host}${baseHref.replace(/\/$/, '')}${elLink}`;
  }
}
